import React, { useEffect, useState } from 'react';
import useDataGrid from '@afosto/utils-react/datagrid/useDataGrid';
import useIntl from '@afosto/utils-react/intl/useIntl';
import usePermissions from '@afosto/utils-react/permissions/usePermissions';
import usePrivate from '@afosto/auth-react/usePrivate';
import { useHistory } from 'react-router-dom';
import Card from '@material-ui/core/Card';
import CardContent from '@material-ui/core/CardContent';
import CardActionArea from '@material-ui/core/CardActionArea';
import Grid from '@material-ui/core/Grid';
import Hidden from '@material-ui/core/Hidden';
import Skeleton from '@material-ui/lab/Skeleton';
import Typography from '@material-ui/core/Typography';
import PricingRulePreview from './PricingRulePreview';
import { DataGrid } from '@afosto/app-components-react/DataGrid';
import DataList from '@afosto/app-components-react/DataList';
import Page from '@afosto/app-components-react/Page';
import Property from '@afosto/app-components-react/Property';
import camelize from '@afosto/utils/string/camelize';
import dataSourceRequest from '@afosto/utils-react/dataSource/dataSourceRequest';
import uuid from '@afosto/utils/uuid';
import { pricing as pricingApi } from '@afosto/order/api/Pricing';
import { datetime } from '@afosto/app-components-react/DataGrid/formatters';
import translations from './translations';

const fetchPricingRules = async dataSource =>
  dataSourceRequest(
    dataSource,
    async (data, requestOptions) => pricingApi.getPricingRules(requestOptions),
    {
      transformData: data => data,
    },
  );

const PricingRulesContainer = props => <Grid container spacing={2} {...props} />;

const PricingRules = () => {
  usePrivate();
  const [hasPermissions, UnauthorizedComponent] = usePermissions(['odr:invoices:read']);
  const intl = useIntl();
  const history = useHistory();
  const [isFetchingPricingRule, setIsFetchingPricingRule] = useState(false);
  const [pricingRule, setPricingRule] = useState(null);

  const dataSource = useDataGrid(fetchPricingRules, {
    modules: ['dataProperties', 'pagination', 'activeItem'],
    pagination: {
      itemsPerPage: 100,
    },
    dataProperties: {
      fixedProperties: [
        {
          key: 'name',
          name: intl.formatMessage({
            id: 'dataGrid.columns.pricingRules.name',
            defaultMessage: 'Name',
            description: 'The column name for column name',
          }),
        },
        {
          key: 'apply_to',
          name: intl.formatMessage({
            id: 'dataGrid.columns.pricingRules.applyTo',
            defaultMessage: 'Apply to',
            description: 'The column name for column apply to',
          }),
          type: 'translation',
          formatOptions: {
            translationKey: 'pricingRuleForm.applyToOptions.{value}',
          },
        },
        {
          key: 'grant_limit',
          name: intl.formatMessage({
            id: 'dataGrid.columns.pricingRules.grantLimit',
            defaultMessage: 'Grant limit',
            description: 'The column name for column grant limit',
          }),
          type: 'number',
        },
        {
          key: 'priority',
          name: intl.formatMessage({
            id: 'dataGrid.columns.pricingRules.priority',
            defaultMessage: 'Priority',
            description: 'The column name for column priority',
          }),
          type: 'number',
        },
        {
          key: 'active_at',
          name: intl.formatMessage({
            id: 'dataGrid.columns.pricingRules.activeAt',
            defaultMessage: 'Activated at',
            description: 'The column name for column active at',
          }),
          type: 'datetime',
        },
        {
          key: 'expires_at',
          name: intl.formatMessage({
            id: 'dataGrid.columns.pricingRules.expiresAt',
            defaultMessage: 'Expires at',
            description: 'The column name for column expires at',
          }),
          type: 'datetime',
        },
        {
          key: 'created_at',
          name: intl.formatMessage({
            id: 'dataGrid.columns.pricingRules.createdAt',
            defaultMessage: 'Created at',
            description: 'The column name for column created at',
          }),
          type: 'datetime',
        },
      ],
    },
  });
  const { activeItem: activeItemModule = {} } = dataSource;
  const { activeItem, setActiveItem } = activeItemModule;

  /**
   * Close the pricing rule preview
   */
  const handleClosePricingRulePreview = () => {
    dataSource.activeItem.setActiveItem(null);
    setPricingRule(null);
  };

  /**
   * Handler that fires after deactivating the pricing rule.
   */
  const handleDeactivatePricingRule = () => {
    dataSource.activeItem.setActiveItem(null);
    setPricingRule(null);
    dataSource.reloadDataSource();
  };

  /**
   * Fetch the pricing rule data if a pricing row is selected and show the data in the preview.
   */
  useEffect(() => {
    let isMounted = true;

    const fetchPricingRule = async id => {
      setIsFetchingPricingRule(true);

      try {
        const response = await pricingApi.getPricingRule(id);
        const { data = {} } = response;

        const formattedFilters = (data?.filters ?? []).reduce(
          (acc, filter) => [...acc, { ...filter, id: uuid() }],
          [],
        );

        const formattedConstraints = (data?.constraints ?? []).reduce(
          (acc, constraint) => [...acc, { ...constraint, id: uuid() }],
          [],
        );

        if (isMounted) {
          setPricingRule({ ...data, filters: formattedFilters, constraints: formattedConstraints });
        }
      } catch (error) {
        if (isMounted) {
          setPricingRule(null);
        }
      } finally {
        if (isMounted) {
          setIsFetchingPricingRule(false);
        }
      }
    };

    if (activeItem) {
      fetchPricingRule(activeItem).catch(() => {
        // catch only.
      });
    } else {
      setPricingRule(null);
    }

    return () => {
      isMounted = false;
    };
  }, [activeItem]);

  const renderPricingRule = pricingRuleData => (
    <Grid item xs={12} key={pricingRuleData?.id}>
      <Card>
        <CardActionArea onClick={() => setActiveItem(pricingRuleData?.id)}>
          <CardContent>
            <Typography variant="subtitle1">{pricingRuleData?.name}</Typography>
            <Typography variant="body2" color="textSecondary">
              {intl.formatMessage({
                id: `pricingRuleForm.applyToOptions.${camelize(pricingRuleData?.apply_to)}`,
                defaultMessage: pricingRuleData?.apply_to,
                description: 'label of the pricing rule apply to value',
              })}
            </Typography>
            <Grid container spacing={1}>
              <Grid item xs={3}>
                <Property
                  label={intl.formatMessage({
                    id: 'dataGrid.columns.pricingRules.grantLimit',
                    defaultMessage: 'Limit',
                    description: 'The column name for column limit',
                  })}
                  value={pricingRuleData?.grant_limit}
                />
              </Grid>
              <Grid item xs={4}>
                <Property
                  label={intl.formatMessage({
                    id: 'dataGrid.columns.pricingRules.priority',
                    defaultMessage: 'Priority',
                    description: 'The column name for column priority',
                  })}
                  value={pricingRuleData?.priority}
                />
              </Grid>
              <Grid item xs={5}>
                <Property
                  label={intl.formatMessage({
                    id: 'dataGrid.columns.pricingRules.activeAt',
                    defaultMessage: 'Active at',
                    description: 'The column name for column active at',
                  })}
                  value={datetime(pricingRuleData?.active_at)}
                />
              </Grid>
            </Grid>
          </CardContent>
        </CardActionArea>
      </Card>
    </Grid>
  );

  const renderPricingRuleSkeleton = key => (
    <Grid item xs={12} key={key}>
      <Card>
        <CardContent>
          <Typography variant="subtitle1">
            <Skeleton variant="text" width="80%" />
          </Typography>
          <Typography variant="body2">
            <Skeleton variant="text" width="50%" />
          </Typography>
          <Grid container spacing={1}>
            <Grid item xs={3}>
              <Property
                label={intl.formatMessage({
                  id: 'dataGrid.columns.pricingRules.limit',
                  defaultMessage: 'Limit',
                  description: 'The column name for column limit',
                })}
                value={<Skeleton variant="text" width="50%" />}
              />
            </Grid>
            <Grid item xs={4}>
              <Property
                label={intl.formatMessage({
                  id: 'dataGrid.columns.pricingRules.priority',
                  defaultMessage: 'Priority',
                  description: 'The column name for column priority',
                })}
                value={<Skeleton variant="text" width="50%" />}
              />
            </Grid>
            <Grid item xs={5}>
              <Property
                label={intl.formatMessage({
                  id: 'dataGrid.columns.pricingRules.activeAt',
                  defaultMessage: 'Active at',
                  description: 'The column name for column active at',
                })}
                value={<Skeleton variant="text" width="80%" />}
              />
            </Grid>
          </Grid>
        </CardContent>
      </Card>
    </Grid>
  );

  if (!hasPermissions()) {
    return (
      <Page title={intl.formatMessage(translations.title)} fullWidth>
        <UnauthorizedComponent />
      </Page>
    );
  }

  return (
    <Grid container wrap="nowrap">
      <Page
        title={intl.formatMessage(translations.title)}
        fullWidth
        style={{ flexGrow: 1 }}
        primaryAction={{
          text: intl.formatMessage(translations.create.button),
          action: () => history.push('/pricing-rules/new'),
        }}
      >
        <Hidden implementation="js" mdUp>
          <DataList
            dataSource={dataSource}
            renderItem={renderPricingRule}
            renderSkeletonItem={renderPricingRuleSkeleton}
            component={PricingRulesContainer}
          />
        </Hidden>
        <Hidden implementation="js" smDown>
          <DataGrid dataSource={dataSource} />
        </Hidden>
      </Page>
      <PricingRulePreview
        isOpen={dataSource.activeItem.activeItem !== null}
        isLoading={isFetchingPricingRule}
        pricingRule={pricingRule}
        onClose={handleClosePricingRulePreview}
        onDeactivatePricingRule={handleDeactivatePricingRule}
      />
    </Grid>
  );
};

export default PricingRules;
